// src/routes/_regular/product-publishing-rules/index.jsx
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Camera, 
  Info, 
  Languages, 
  AlertTriangle, 
  CheckCircle2, 
  ArrowRight,
  HeartHandshake,
  ShoppingBag,
  Sparkles,
  Image,
  XCircle
} from 'lucide-react';

export const Route = createFileRoute('/_regular/product-publishing-rules/')({
  component: ProductPublishingRulesPage,
  head: () => ({
    meta: [
      { title: 'قواعد نشر المنتجات | سكيكيمة' },
      {
        name: 'description',
        content: 'القواعد والمعايير الواجب اتباعها عند نشر المنتجات على سكيكيمة.',
      },
    ],
  }),
});

function ProductPublishingRulesPage() {
  const navigate = useNavigate();

  const handleStartSelling = () => {
    navigate({ to: '/dashboard/create-store' });
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-primary/5" />
        <div className="container relative mx-auto px-4 text-center">
          <Badge variant="outline" className="mb-4 gap-1 border-primary/30 bg-primary/5 text-primary">
            <FileText className="h-3 w-3" />
            دليل النشر
          </Badge>
          <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            قواعد نشر المنتجات في{" "}
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              سكيكيمة
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            نحرص على تقديم تجربة تسوق نظيفة وآمنة. اتبع هذه الإرشادات لضمان قبول منتجاتك ووصولها إلى آلاف العملاء.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button onClick={handleStartSelling} size="lg" className="gap-2 rounded-full">
              <ShoppingBag className="h-4 w-4" />
              ابدأ البيع الآن
            </Button>
            <Button variant="outline" size="lg" className="gap-2 rounded-full">
              <HeartHandshake className="h-4 w-4" />
              تواصل مع فريق الدعم
            </Button>
          </div>
        </div>
      </section>

      {/* Main content: rules grid */}
      <div className="container mx-auto px-4 pb-20">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          
          {/* Card 1: المحتوى المحظور */}
          <Card className="group border-border/40 bg-card/80 shadow-sm transition-all hover:shadow-md">
            <CardHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <CardTitle>المحتوى المحظور تماماً</CardTitle>
              <CardDescription>
                لن يتم قبول المنتجات التي تحتوي على:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <ul className="list-disc space-y-1 pr-5">
                <li>كلمات أو عبارات مسيئة، عنصرية، أو غير أخلاقية</li>
                <li>صور أو رسوم بشرية أو حيوانية (بما في ذلك الصور الفوتوغرافية والرسومات)</li>
                <li>منتجات تنتهك الخصوصية أو القوانين المحلية</li>
                <li>محتويات دينية أو سياسية غير مرخصة</li>
              </ul>
            </CardContent>
          </Card>

          {/* Card 2: جودة الصور والخلفية */}
          <Card className="group border-border/40 bg-card/80 shadow-sm transition-all hover:shadow-md">
            <CardHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Camera className="h-6 w-6" />
              </div>
              <CardTitle>جودة الصور والخلفية</CardTitle>
              <CardDescription>
                اجعل منتجك يبرز بوضوح واحترافية
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <span><strong>خلفية نظيفة وموحدة</strong> – يفضل أبيض أو بلون واحد بسيط، بدون فوضى</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <span><strong>المنتج فقط في الإطار</strong> – بدون أشخاص، حيوانات، أو عناصر تشتيت</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <span><strong>دقة عالية وإضاءة جيدة</strong> – مقبول 800x800 بكسل على الأقل</span>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <span>لا يُسمح بالصور المفبركة أو المائية أو الملصقات التجارية على المنتج</span>
              </div>
            </CardContent>
          </Card>

          {/* Card 3: التفاصيل والوصف */}
          <Card className="group border-border/40 bg-card/80 shadow-sm transition-all hover:shadow-md">
            <CardHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Info className="h-6 w-6" />
              </div>
              <CardTitle>معلومات المنتج الكاملة</CardTitle>
              <CardDescription>
                دقة المعلومات تبني ثقة العميل
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <ul className="list-disc space-y-1 pr-5">
                <li>عنوان واضح يعكس المنتج بدقة</li>
                <li>وصف مفصل يشمل: المواد، الأبعاد، اللون، الحالة، والميزات</li>
                <li>تحديد الفئة والعلامة التجارية (إن وجدت)</li>
                <li>السعر والكمية والضمان (إذا كان متاحاً)</li>
                <li>إضافة مقاطع فيديو قصيرة (اختياري لكن محفّز)</li>
              </ul>
            </CardContent>
          </Card>

          {/* Card 4: اللوائح الخاصة بالملابس */}
          <Card className="group border-border/40 bg-card/80 shadow-sm transition-all hover:shadow-md">
            <CardHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10 text-accent-foreground">
                <Languages className="h-6 w-6" />
              </div>
              <CardTitle>ملابس وأزياء – متطلبات إضافية</CardTitle>
              <CardDescription>
                للملابس والإكسسوارات
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>تقديم جدول المقاسات واضح (بالسنتيمتر أو الإنش).</p>
              <p>إبراز نسيج القماش (قطن، بوليستر، صوف إلخ).</p>
              <p>التأكيد على خلو الصور من عارضين بشريين – يمكن استخدام مانيكان أو تعليق المنتج.</p>
              <p>توضيح تعليمات الغسيل إذا أمكن.</p>
            </CardContent>
          </Card>

          {/* Card 5: الإجراءات المخالفة والعقوبات */}
          <Card className="group border-border/40 bg-card/80 shadow-sm transition-all hover:shadow-md">
            <CardHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <CardTitle>مخالفة القواعد</CardTitle>
              <CardDescription>
                عواقب عدم الامتثال
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>• رفض نشر المنتج مع إشعار بالتعديلات المطلوبة.</p>
              <p>• حذف المنتج في حال التكرار، مع إنذار للمتجر.</p>
              <p>• تعليق حساب المتجر في حال الانتهاكات الجسيمة أو المتكررة.</p>
              <p>• الحق في إلغاء العضوية في المنصة نهائياً في حال خرق شروط الاستخدام.</p>
            </CardContent>
          </Card>

          {/* Card 6: نصائح لتحسين القبول */}
          <Card className="group border-border/40 bg-card/80 shadow-sm transition-all hover:shadow-md">
            <CardHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <CardTitle>نصائح إضافية لقبول سريع</CardTitle>
              <CardDescription>
                توجيهات ذهبية لتجربة أفضل
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <ul className="list-disc space-y-1 pr-5">
                <li>قم بتحميل 3-5 صور من زوايا مختلفة.</li>
                <li>تأكد من أن الخلفية لا تحتوي على نصوص أو علامات مائية.</li>
                <li>اكتب الوصف باللغة العربية الفصحى أو العامية المفهومة.</li>
                <li>تجنب الإشارة إلى منصات أخرى أو وسائل تواصل خارج سكيكيمة.</li>
                <li>استخدم كلمات مفتاحية مناسبة لتظهر في نتائج البحث.</li>
              </ul>
            </CardContent>
          </Card>
        </div>

{/* Additional section: example of good vs bad */}
<div className="mt-20">
  <div className="text-center mb-12">
    <h2 className="text-3xl font-bold tracking-tight mb-4">أمثلة تطبيقية</h2>
    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
      فهم الفرق بين الصور المقبولة والمرفوضة يساعدك على نشر منتجاتك بشكل أسرع
    </p>
  </div>

  <div className="grid md:grid-cols-2 gap-8">
    {/* Good Example Card */}
    <div className="group relative rounded-xl border border-border/50 bg-gradient-to-br from-emerald-50/50 to-background p-6 transition-all hover:shadow-lg dark:from-emerald-950/20">
      <div className="absolute -top-3 right-6">
        <div className="flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
          <CheckCircle2 className="h-4 w-4" />
          <span>مثال مقبول</span>
        </div>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div className="relative aspect-square overflow-hidden rounded-xl bg-white p-4 shadow-sm">
            <img
              src="https://www.beyours.in/cdn/shop/files/light-blue-classic-shirt.jpg?v=1763537464&width=300"
              alt="نموذج منتج مقبول"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="gap-1">
              <Camera className="h-3 w-3" />
              خلفية واضحة
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <Sparkles className="h-3 w-3" />
              جودة عالية
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <HeartHandshake className="h-3 w-3" />
              بدون علامات
            </Badge>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-xl font-semibold">قميص قطني – لون أزرق فاتح</h3>
          <p className="text-sm text-muted-foreground">
            قميص رجالي مصنوع من قطن 100%، ناعم، مقاس M، خلفية واضحة، صورة واضحة بدون عارض.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>خلفية موحدة </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>المنتج يملأ 85% من الإطار على الأقل</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>صورة واضحة بدون علامات مائية أو نصوص</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Bad Example Card */}
    <div className="group relative rounded-xl border border-border/50 bg-gradient-to-br from-rose-50/50 to-background p-6 transition-all hover:shadow-lg dark:from-rose-950/20">
      <div className="absolute -top-3 right-6">
        <div className="flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1 text-sm font-medium text-rose-700 dark:bg-rose-900/40 dark:text-rose-400">
          <XCircle className="h-4 w-4" />
          <span>مثال مرفوض</span>
        </div>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div className="relative aspect-square overflow-hidden rounded-xl bg-rose-50/30 p-4 shadow-sm">
            <img
              src="https://mrvastra.com/cdn/shop/articles/Blue_Shirt_and_Matching_Pant_Combinations_for_Men_cbf3b62a-e91f-4dc7-bb9f-c49e972da9d8.jpg?v=1780141136"
              alt="نموذج منتج مرفوض"
              className="h-full w-full object-contain opacity-80"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/5">
              <XCircle className="h-12 w-12 text-rose-500/50" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="destructive" className="gap-1">
              <Image className="h-3 w-3" />
              خلفية مزدحمة
            </Badge>
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              علامات مائية
            </Badge>
            <Badge variant="destructive" className="gap-1">
              <Info className="h-3 w-3" />
              وجود عارض
            </Badge>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-xl font-semibold">قميص (بصورة شخص وعلامة مائية)</h3>
          <p className="text-sm text-muted-foreground">
            وصف غير واضح، خلفية فوضوية، وجود شعار موقع آخر، رجل يظهر في الصورة.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <XCircle className="h-4 w-4 text-rose-600" />
              <span>خلفية مشغولة تحتوي على عناصر تشتيت</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <XCircle className="h-4 w-4 text-rose-600" />
              <span>وجود شخص أو حيوان في الصورة</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <XCircle className="h-4 w-4 text-rose-600" />
              <span>علامات مائية أو شعارات مواقع أخرى</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* Summary strip */}
  <div className="mt-8 flex flex-wrap items-center justify-center gap-4 rounded-xl bg-muted/30 p-4 text-center">
    <div className="flex items-center gap-2">
      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
      <span className="text-sm font-medium">منتج مقبول</span>
    </div>
    <div className="text-muted-foreground">•</div>
    <div className="flex items-center gap-2">
      <XCircle className="h-5 w-5 text-rose-600" />
      <span className="text-sm font-medium">منتج مرفوض</span>
    </div>
    <div className="text-muted-foreground">•</div>
    <div className="flex items-center gap-2">
      <Camera className="h-5 w-5 text-primary" />
      <span className="text-sm font-medium">خلفية بيضاء نقية</span>
    </div>
  </div>
</div>

        {/* CTA at bottom */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center justify-center gap-4 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 p-8">
            <h2 className="text-2xl font-bold">جاهز لنشر منتجك وفق القواعد؟</h2>
            <p className="max-w-md text-muted-foreground">
              التزم بهذه الإرشادات واستفد من وصولك إلى آلاف المتسوقين اليوم.
            </p>
            <Button onClick={handleStartSelling} size="lg" className="gap-2 rounded-full">
              أنشئ متجرك الآن
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}